import { DateTime } from "luxon";
import { CONTROL_HEIGHT_PX } from "../../../../theme/tokens";
import { trackErrorEvent } from "../../../../utils/analytics";
import { getEffectiveDayStart, getShiftTypeFromStartTime, isAffectByDayStart, isBeforeDayStart, resolveDayCell } from "../../../../utils/dateUtils";
import type { ActualNurse, BaseNurseShift, DepartmentLocation, Job, NurseInShift, WeekData } from "../../../../utils/models";
import { ShiftType } from "../../../../utils/models";
import { filterNursesForCoverage } from "../../../../utils/statsCalculations";

/** Columns in one rendered week row — also the length of `getMonthlyVisibleWeek`'s result. */
export const DAYS_IN_WEEK = 7;

/**
 * One calendar day for the grid: the Day and Night shift buckets a week is split into are merged into
 * a single list of scheduled (and actual) shifts, since the Monthly view groups by job, not by the
 * Day/Night rows the weekly grid uses.
 *
 * INVARIANT — day bucketing is ALREADY DONE, upstream, and must never be redone here.
 * `backend/services/department.py` keys every shift by `get_staffing_date(start_time, day_start,
 * timezone, early_buffer_minutes)` before it ever reaches `weeklyShifts[].days[]`, so a 2am shift on a
 * 06:00-day-start department arrives already filed under the PREVIOUS calendar day — which is the day
 * the scheduler calls it. `getMergedDay` therefore concatenates the period buckets VERBATIM. Do not add
 * `getShiftDisplayDate`/`getStaffingDate` to this aggregation: it would apply the boundary a second time
 * and move every night shift one more day. The department's day start is still needed downstream, but
 * only for ORDERING, ANCHORING and BANDING a window (see `MonthlyDayBoundary`) — never for membership.
 */
export type MonthlyDayData = {
	date: DateTime;
	nurses: NurseInShift[];
	nursesActual: ActualNurse[];
};

export const getMergedDay = (week: WeekData | undefined, dayIndex: number): MonthlyDayData | undefined => {
	const firstDay = week?.weeklyShifts[0]?.days[dayIndex];
	if (!firstDay) return undefined;
	const nurses: NurseInShift[] = [];
	const nursesActual: ActualNurse[] = [];
	for (const shift of week?.weeklyShifts ?? []) {
		const day = shift.days[dayIndex];
		if (!day) continue;
		nurses.push(...day.nurses);
		if (day.nursesActual) nursesActual.push(...day.nursesActual);
	}
	return { date: firstDay.date, nurses, nursesActual };
};

/** One resolved column of one visible week row: the calendar day plus where its data was read from. */
export type MonthlyVisibleDay = {
	/** The calendar date this column shows — correct even when `mergedDay` is undefined. */
	date: DateTime;
	/** Index into the contiguous `hospitalData.weeks`; `weekIndex + 1` for a column that spills. */
	sourceWeekIndex: number;
	/** Sunday-indexed day within `sourceWeekIndex` (`days[0]` is always Sunday). */
	sourceDayIndex: number;
	/** Undefined when the source data-week hasn't been fetched — the cell renders empty. */
	mergedDay: MonthlyDayData | undefined;
};

/**
 * First calendar day of the visible window anchored on data-week `weekIndex`.
 *
 * `weeklyShifts[0].days[0]` is ALWAYS Sunday (see `resolveDayCell` in utils/dateUtils.ts) regardless of
 * the department's configured first-day-of-week, so the visible start is that Sunday plus `offset` days
 * — where `offset` is `getVisibleWeekOffset(weekStartDay)`. `offset === 0` is the identity.
 */
export const getMonthlyVisibleWeekStart = ({ weeks, weekIndex, offset }: { weeks: WeekData[]; weekIndex: number; offset: number }): DateTime | undefined => {
	const sunday = weeks[weekIndex]?.weeklyShifts[0]?.days[0]?.date;
	return sunday?.plus({ days: offset });
};

/**
 * The 7 columns of one visible week row, resolved through the shared `resolveDayCell` map.
 *
 * Backend weeks are Sunday-indexed, so a Monday-start (or any non-Sunday) department renders a ROTATED
 * window: columns `0..6-offset` come from `weekIndex`, the rest spill into `weekIndex + 1`. Indexing
 * `weeklyShifts[0].days[column]` directly — as this grid used to — silently renders Sunday-aligned weeks
 * for every department.
 *
 * Each date is derived from the base week's Sunday anchor rather than from the resolved cell, so the
 * trailing spill at the end of `hospitalData.weeks` still shows the right calendar day with an empty
 * `mergedDay` (a partial last row) instead of throwing. Returns `[]` when the base week itself is
 * missing — there is then no anchor to date the row from at all.
 */
export const getMonthlyVisibleWeek = ({ weeks, weekIndex, offset }: { weeks: WeekData[]; weekIndex: number; offset: number }): MonthlyVisibleDay[] => {
	const visibleStart = getMonthlyVisibleWeekStart({ weeks, weekIndex, offset });
	if (!visibleStart) return [];
	return Array.from({ length: DAYS_IN_WEEK }, (_, column) => {
		const { sourceWeekIndex, sourceDayIndex } = resolveDayCell(weekIndex, column, offset);
		return {
			date: visibleStart.plus({ days: column }),
			sourceWeekIndex,
			sourceDayIndex,
			mergedDay: getMergedDay(weeks[sourceWeekIndex], sourceDayIndex),
		};
	});
};

/**
 * Job-centric Monthly staffing view (APP-465).
 *
 * The screen shows per-day totals grouped by JOB (Nurse, Aide, …) and by the department's REAL
 * shift-time windows, and drills into a per-day sidebar grouped by exact shift-time window → job →
 * employees. This module holds the pure aggregation used by the grid cells and the sidebar so the two
 * can never disagree.
 */

/** How the grid cell breaks a day's totals down. `ByJob` is the default (ticket MVP #5). */
export enum StaffingTotalsMode {
	ByJob = "byJob",
	ByShift = "byShift",
	JobAndShift = "jobAndShift",
}

/** Scheduled (or actual) shifts on a day that count toward staffing — excludes trainees/pending-delete. */
export const getCountableDayShifts = (day: MonthlyDayData | undefined, showActual: boolean): BaseNurseShift[] => {
	if (!day) return [];
	const shifts: BaseNurseShift[] = showActual ? day.nursesActual : day.nurses;
	return shifts.filter((shift) => filterNursesForCoverage(shift, false));
};

// ---------------------------------------------------------------------------
// The department's day boundary — ordering, anchoring and banding only
// ---------------------------------------------------------------------------

/**
 * The department's staffing-day boundary, as ONE value threaded through this module.
 *
 * Read it as "where does this department's day begin", never as "which day is this shift on" — the
 * second question is already answered by the backend (see the invariant on `MonthlyDayData`). It exists
 * because a clock time alone cannot order, anchor or band a window: on a 06:00→06:00 staffing day a
 * 02:00 window is the END of the day, yet `startOf("day")` sorts it FIRST and the hardcoded 6–18 split
 * in `getShiftTypeFromStartTime` cannot see a department whose day starts in the evening at all.
 *
 * One object rather than two positional arguments so a call site cannot pass the grace without the
 * boundary (or, worse, in the wrong order) — every consumer takes the pair or neither.
 */
export type MonthlyDayBoundary = {
	/** `chosenDepartment.dayStart` — `"HH:mm"` or `"HH:mm:ss"`. */
	dayStart: string;
	/** `chosenDepartment.scheduled_shift_delta_minutes` — the SCHEDULED-shift grace. `0` turns it off. */
	graceMinutes: number;
};

/** The boundary a shift is measured against: the configured day start, pulled back by the grace. */
const effectiveBoundaryOf = (boundary: MonthlyDayBoundary): string => getEffectiveDayStart(boundary.dayStart, boundary.graceMinutes);

/**
 * Whether a window starts in the WRAP-AROUND portion of the staffing day — the stretch that belongs to
 * the day it is filed under but falls on the other side of midnight from it.
 *
 * For an AM boundary (06:00) that is the small hours: a 02:00 start is the tail of the previous
 * evening's day. For a PM boundary (20:00) it is the evening: a 21:00 start opens the NEXT staffing
 * day. Either way it names the shifts a calendar-day reading loses, which is exactly why they are
 * promoted past the occurrence threshold in `deriveShiftWindows`.
 */
export const isWrapAroundWindowStart = (start: DateTime, boundary: MonthlyDayBoundary): boolean => isBeforeDayStart(start, effectiveBoundaryOf(boundary));

/**
 * The instant a window's clock hour occupies on a given staffing day — the single anchor every ordered
 * list on this screen sorts by (chip row, legend, tile rows, drawer rows).
 *
 * Delegates to `isAffectByDayStart`, the same helper `calculateShiftTimes` uses to place a real shift,
 * so a requirement-only 02:00 row and a covered 02:00 row land on the SAME instant and therefore in the
 * same position. Without a boundary it degrades to the plain calendar anchor, which is what the screen
 * did before a day start was threaded in.
 */
export const anchorWindowTime = (staffingDate: DateTime, hour: number, minute: number, boundary: MonthlyDayBoundary | undefined): DateTime =>
	boundary === undefined
		? staffingDate.startOf("day").set({ hour, minute, second: 0, millisecond: 0 })
		: isAffectByDayStart(staffingDate, hour, minute, boundary.dayStart, boundary.graceMinutes);

/** Half a day in minutes — where a staffing day flips from its light half to its dark half. */
const HALF_DAY_MINUTES = 12 * 60;
const MINUTES_IN_DAY = 24 * 60;

const minutesOfDay = (time: { hour: number; minute: number }): number => time.hour * 60 + time.minute;

/**
 * Accept only an in-range `"HH:mm"` or `"HH:mm:ss"` — the shape every clock time reaching this module
 * is declared to have (`chosenDepartment.dayStart`, `DepartmentLocation.shift_start`/`shift_end`).
 * Rejects `"07:00junk"` (which slicing alone would have turned into a valid `"07:00"`), out-of-range
 * times like `"06:99"`, and bad seconds like `"07:00:99"`.
 */
const CLOCK_TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/;

/**
 * Minutes since midnight for a clock time, or `null` when the string is not one.
 *
 * Explicitly nullable rather than `NaN`-on-failure: `NaN` makes every downstream comparison false, and
 * `resolveWindowBand` is all comparisons — a malformed day start would have banded the department's
 * ENTIRE taxonomy `Day` with nothing to show for it. The caller reports and falls back instead.
 */
const parseBoundaryMinutes = (boundary: string): number | null => {
	if (!CLOCK_TIME_PATTERN.test(boundary)) return null;
	const [hour, minute] = boundary.split(":");
	return Number(hour) * 60 + Number(minute);
};

/**
 * The window in which "which half of the staffing day does this open in" is a meaningful question.
 *
 * The rule below splits the staffing day in half at its boundary and reads the first half as the one
 * that matches the boundary's own light/dark register. That only holds while the boundary actually
 * OPENS a morning or an evening. Near midnight and near noon it silently inverts: at a `00:00` day
 * start the halves are midnight–noon and noon–midnight, so a 2am window would be banded Day (sun) and a
 * 2pm one Night (moon); at exactly `12:00` the same thing happens the other way round. Those are wrong
 * answers, not degraded ones, so outside these ranges the rule yields to the absolute
 * `getShiftTypeFromStartTime` split, which is at least never inverted.
 *
 * The value compared is the EFFECTIVE boundary — the day start pulled back by the scheduled-shift grace
 * — so the morning floor is 04:00 rather than 06:00: a 06:00 department with the common 2h grace
 * measures exactly 04:00 and must still qualify. A grace deep enough to open the staffing day before
 * 4am has no "light half" left to name, and falls back.
 */
const EARLIEST_MORNING_BOUNDARY_MINUTES = 4 * 60;
const LATEST_MORNING_BOUNDARY_MINUTES = 11 * 60;
const EARLIEST_EVENING_BOUNDARY_MINUTES = 16 * 60;
const LATEST_EVENING_BOUNDARY_MINUTES = 23 * 60;

const opensARecognizableHalfDay = (boundaryMinutes: number): boolean =>
	(boundaryMinutes >= EARLIEST_MORNING_BOUNDARY_MINUTES && boundaryMinutes <= LATEST_MORNING_BOUNDARY_MINUTES) ||
	(boundaryMinutes >= EARLIEST_EVENING_BOUNDARY_MINUTES && boundaryMinutes <= LATEST_EVENING_BOUNDARY_MINUTES);

/**
 * Which band (Day / Night) a window belongs to, measured from the department's own day start rather
 * than from a hardcoded 6am–6pm window.
 *
 * The rule: split the staffing day in half at its boundary and ask which half the window opens in. An
 * AM boundary opens the day with its LIGHT half (06:00 → day shifts, then 18:00 → nights); a PM
 * boundary opens it with its DARK half (20:00 → nights, then 08:00 → days). `isAmTime`'s noon pivot is
 * what tells the two apart, and it is the same pivot `getStaffingDate` already turns on.
 *
 * Two things this fixes that the absolute 6–18 split could not:
 *  - a department whose day starts in the evening, where "first half of the staffing day" and "daytime"
 *    are opposites;
 *  - a grace window, where a 05:30 start on a 06:00/2h-grace department IS that morning's day shift and
 *    the absolute rule banded it Night (and painted it indigo with a moon).
 *
 * `getShiftTypeFromStartTime` stays the fallback for a caller with no day start to offer, so nothing
 * regresses where the boundary is unknown — and it is also the fallback for the two cases where this
 * rule cannot answer honestly: a MALFORMED day start (reported, never silently absorbed) and a boundary
 * outside `opensARecognizableHalfDay`'s window, where the half-day split inverts near midnight and noon.
 */
export const resolveWindowBand = (start: DateTime, boundary: MonthlyDayBoundary | undefined): ShiftType => {
	if (boundary === undefined) return getShiftTypeFromStartTime(start);
	const effective = effectiveBoundaryOf(boundary);
	const boundaryMinutes = parseBoundaryMinutes(effective);
	if (boundaryMinutes === null) {
		// Surfaced, not silently dropped — the same treatment a malformed location window gets above, and
		// under its own event name so the two are distinguishable. Without this the band math runs on `NaN`,
		// every comparison in it is false, and EVERY window in the department bands `Day`.
		trackErrorEvent("monthly_view_invalid_day_start", new Error(`Malformed department day start: ${boundary.dayStart}`), {
			day_start: boundary.dayStart,
			grace_minutes: boundary.graceMinutes,
			effective_day_start: effective,
		});
		return getShiftTypeFromStartTime(start);
	}
	if (!opensARecognizableHalfDay(boundaryMinutes)) return getShiftTypeFromStartTime(start);
	const sinceDayStart = (minutesOfDay(start) - boundaryMinutes + MINUTES_IN_DAY) % MINUTES_IN_DAY;
	const opensInFirstHalf = sinceDayStart < HALF_DAY_MINUTES;
	// An AM boundary opens the staffing day in daylight; a PM one opens it at night.
	const firstHalfIsDay = boundaryMinutes <= HALF_DAY_MINUTES;
	return opensInFirstHalf === firstHalfIsDay ? ShiftType.Day : ShiftType.Night;
};

// ---------------------------------------------------------------------------
// The explicit home for a shift carrying no job
// ---------------------------------------------------------------------------

/**
 * The job id a shift with no `jobId` counts under.
 *
 * This screen used to DROP those shifts: `passesFilters` returned false on a missing job, so an
 * unassigned body vanished from the tile's headline, its job pills, its By-shift rows AND the day
 * drawer at once — the totals simply disagreed with the schedule, silently. A shift with no job is a
 * real person on a real shift; the honest answer is to give it a named home rather than to hide it, so
 * a scheduler sees "3 unassigned" and can act on it.
 *
 * Deliberately not a value any real `Job.id` can collide with.
 */
export const MONTHLY_UNASSIGNED_JOB_ID = "monthly-unassigned-job";

/** The job a shift counts under — its own, or the explicit unassigned bucket. Never "nowhere". */
export const resolveShiftJobId = (shift: BaseNurseShift): string => shift.jobId ?? MONTHLY_UNASSIGNED_JOB_ID;

/**
 * The synthetic `Job` the unassigned bucket is aggregated as, so every consumer (count pills, window
 * splits, drawer groups, job chips) treats it as just another job instead of each growing a branch.
 *
 * The caller owns the label because this module is pure — pass `t("monthly.view.job.unassigned")`.
 * Sorted last: it is the residue of the job taxonomy, not a member of it.
 */
export const buildUnassignedJob = (displayName: string): Job => ({
	id: MONTHLY_UNASSIGNED_JOB_ID,
	name: MONTHLY_UNASSIGNED_JOB_ID,
	displayName,
	sortOrder: Number.MAX_SAFE_INTEGER,
	excludeFromPpd: false,
});

// ---------------------------------------------------------------------------
// Shift-time windows
// ---------------------------------------------------------------------------

/**
 * How many shift-time windows are DERIVED for the rendered span — the size of the taxonomy the filter chip
 * row, the legend and the sidebar all share. Everything past the cap lands in the `Other` bucket, so no
 * shift goes uncounted, and the filter row can't wrap into a second line (every wrapped line steals height
 * from the six grid rows).
 *
 * Deliberately NOT the number of rows a grid cell draws: how many windows exist is a property of the
 * department, how many fit in a 130px tile is a property of the layout. `MONTHLY_MAX_CELL_WINDOW_ROWS`
 * owns the second question, and the two move independently on purpose — a cell showing 3 of 4 derived
 * windows is exactly what the `+N more` affordance is for.
 */
export const MONTHLY_MAX_SHIFT_WINDOWS = 4;

/**
 * How often an hour-rounded window has to occur across the WHOLE rendered span to earn a chip. Mirrors
 * `getPopularShiftTimes`' `amount > 1` in `TimeFilterChips`: a window seen once in six weeks is a
 * one-off, not part of the department's shift pattern. A window the department *configures* a
 * requirement for bypasses the threshold entirely (see `deriveShiftWindows`).
 */
export const MONTHLY_MIN_SHIFT_WINDOW_OCCURRENCES = 2;

/** Key of the overflow bucket: every shift outside the capped window set, so none goes uncounted. */
export const MONTHLY_OTHER_WINDOW_KEY = "other";

/**
 * One shift-time window the screen groups by. Hour-rounded on BOTH ends — that is what makes the chip
 * row stable and short; the exact `HH:mm` windows (`2p–10:30p`) survive one level down, in the sidebar.
 *
 * Only the CLOCK TIME of `start`/`end` is meaningful to the KEY — both are formatted `HH:mm`, so the
 * key stays reference-day independent however the pair is anchored, and an overnight window's `end` is
 * deliberately NOT rolled to the next day.
 *
 * The anchoring of `start` is meaningful to the ORDER, though: it is placed on the caller's
 * `referenceDate` through `anchorWindowTime`, so on a 06:00→06:00 staffing day a 02:00 window sorts
 * last (where the department puts it) rather than first (where the calendar does). One order then holds
 * across the chip row, the legend, the tile rows and the drawer, because all four sort by this instant.
 */
export type MonthlyShiftWindow = {
	/** `"HH:mm-HH:mm"` over the rounded pair, or `MONTHLY_OTHER_WINDOW_KEY` for the overflow bucket. */
	key: string;
	/** `null` on the `Other` bucket, which spans no single range. */
	start: DateTime | null;
	end: DateTime | null;
	/** `"6a–2p"`. Empty on the `Other` bucket — the caller supplies its translated label. */
	label: string;
	/** Day or Night via `resolveWindowBand` (day-start relative); `null` on `Other` (no band → neutral). */
	band: ShiftType | null;
	/** Position of this window WITHIN its band, ordered by start — the tint-ramp step. `0` on `Other`. */
	bandIndex: number;
	/** True when a `DepartmentLocation` config bears a requirement for this window. */
	hasTarget: boolean;
};

/** The band-less overflow bucket. Also the presentation fallback for a window outside the offered set. */
export const MONTHLY_OTHER_WINDOW: MonthlyShiftWindow = {
	key: MONTHLY_OTHER_WINDOW_KEY,
	start: null,
	end: null,
	label: "",
	band: null,
	bandIndex: 0,
	hasTarget: false,
};

/** Rounds a shift boundary to the hour, the way `findDuplicateTimeSlots` buckets duplicate time slots. */
const roundToHour = (time: DateTime): DateTime => time.set({ minute: 0, second: 0, millisecond: 0 });

/** `"6a"`, `"7a"`, `"3p"`, `"10:30p"` — the compact shift-hour label shared by the chips and the sidebar. */
export const formatShiftHour = (time: DateTime): string => {
	const suffix = time.hour < 12 ? "a" : "p";
	const hour12 = time.hour % 12 === 0 ? 12 : time.hour % 12;
	return time.minute === 0 ? `${hour12}${suffix}` : `${hour12}:${time.minute.toString().padStart(2, "0")}${suffix}`;
};

const formatWindowLabel = (start: DateTime, end: DateTime): string => `${formatShiftHour(start)}–${formatShiftHour(end)}`;

/** Stable key for an EXACT start/end pair — the sidebar's grain, where `:30` windows survive. */
export const exactWindowKey = (start: DateTime, end: DateTime): string => `${start.toFormat("HH:mm")}-${end.toFormat("HH:mm")}`;

/** Stable key for the HOUR-ROUNDED pair — the chip/cell grain, and what `deriveShiftWindows` returns. */
export const roundedWindowKey = (start: DateTime, end: DateTime): string => exactWindowKey(roundToHour(start), roundToHour(end));

/** The window label to render: the rounded range, or the caller's translated `Other` copy. */
export const monthlyWindowLabel = (window: MonthlyShiftWindow, otherLabel: string): string =>
	window.key === MONTHLY_OTHER_WINDOW_KEY ? otherLabel : window.label;

/** A configured location window boundary, validated against the module's one `CLOCK_TIME_PATTERN`. */
const parseLocationTime = (raw: string): DateTime =>
	CLOCK_TIME_PATTERN.test(raw) ? DateTime.fromFormat(raw.slice(0, 5), "HH:mm") : DateTime.invalid("malformed location time");

type WindowCandidate = { key: string; start: DateTime; end: DateTime; occurrences: number; hasTarget: boolean; isWrapAround: boolean };

/**
 * The department's REAL shift-time windows for the rendered span, hour-rounded, capped, with an `Other`
 * overflow bucket.
 *
 * Derived the way the Day view builds its time filters — `findDuplicateTimeSlots` semantics: round BOTH
 * ends to the hour and frequency-count. The count runs across the WHOLE rendered span rather than
 * per-day on purpose: a per-day derivation would rebuild the chip row on every page turn (and give two
 * neighbouring cells different taxonomies).
 *
 * Requirement-bearing windows from `chosenDepartment.locations` are UNIONED in and bypass the occurrence
 * threshold, so a window that is fully uncovered (0 scheduled / N required) still earns its chip and its
 * cell row instead of vanishing — the coverage gap is the whole point of the screen.
 *
 * A WRAP-AROUND window bypasses the threshold the same way, for the same reason. A 2a–6p shift on a
 * 06:00-day-start department is the tail of its staffing day, it is rare by nature, and a department
 * that configures only 06:00 and 18:00 starts gives it no requirement to ride in on — so under the
 * occurrence rule alone it earned no chip, no cell row and no band, and the one person working it was
 * invisible everywhere but the drawer. Making it a day-START rule rather than an occurrence rule is
 * what makes a night structurally unloseable — and it bypasses the CAP too, holding one reserved slot
 * against the least-used window that would otherwise have taken it (see below), because clearing the
 * threshold only to lose to the cap would leave it exactly where it started.
 *
 * Ranking is by occurrences (a configured, then a wrap-around window breaking a tie), then start time
 * for determinism; the top `maxWindows` are returned in START order — the department's OWN order, since
 * every candidate is anchored through `anchorWindowTime` — and `Other` is appended when anything was
 * left out.
 */
export const deriveShiftWindows = ({
	days,
	visibleJobs,
	locations,
	referenceDate,
	dayBoundary,
	maxWindows = MONTHLY_MAX_SHIFT_WINDOWS,
}: {
	/** Every rendered day, paired with whether that day reads actual or scheduled shifts. */
	days: readonly { day: MonthlyDayData | undefined; showActual: boolean }[];
	visibleJobs: Job[];
	locations: DepartmentLocation[];
	/** Staffing day the returned `start`/`end` clock times are anchored on. Only the time matters. */
	referenceDate: DateTime;
	/** The department's day boundary. Omit only where one genuinely isn't available. */
	dayBoundary?: MonthlyDayBoundary;
	maxWindows?: number;
}): MonthlyShiftWindow[] => {
	const visibleJobIds = new Set(visibleJobs.map((job) => job.id));
	const candidates = new Map<string, WindowCandidate>();

	const upsert = (start: DateTime, end: DateTime, { occurred, hasTarget }: { occurred: boolean; hasTarget: boolean }): void => {
		// Anchored on the department's day, not the calendar's: a 02:00 start on a 06:00→06:00 day lands
		// at the END of the staffing day, which is where every ordered list on this screen must put it.
		const roundedStart = anchorWindowTime(referenceDate, start.hour, 0, dayBoundary);
		// The end rides on the start's own calendar day so the pair moves together. Deliberately NOT
		// rolled past midnight for an overnight window — the key is clock-time only (`exactWindowKey`
		// formats `HH:mm`), and rolling would suggest a duration this taxonomy does not claim to carry.
		const roundedEnd = roundedStart.startOf("day").set({ hour: end.hour });
		const key = exactWindowKey(roundedStart, roundedEnd);
		const existing = candidates.get(key);
		if (!existing) {
			const isWrapAround = dayBoundary !== undefined && isWrapAroundWindowStart(roundedStart, dayBoundary);
			candidates.set(key, { key, start: roundedStart, end: roundedEnd, occurrences: occurred ? 1 : 0, hasTarget, isWrapAround });
			return;
		}
		if (occurred) existing.occurrences += 1;
		if (hasTarget) existing.hasTarget = true;
	};

	for (const entry of days) {
		for (const shift of getCountableDayShifts(entry.day, entry.showActual)) {
			// A shift with no job counts under the explicit unassigned bucket rather than being skipped —
			// otherwise a department's only 2a shift could fail to define a window purely for lacking a job.
			if (!visibleJobIds.has(resolveShiftJobId(shift))) continue;
			upsert(shift.startTime, shift.endTime, { occurred: true, hasTarget: false });
		}
	}

	for (const location of locations) {
		if (!location.location) continue;
		// Only a config that actually asks for bodies defines a window; a 0/0 row is a disabled slot.
		if (!(location.amountRequired > 0 || location.amount > 0)) continue;
		const start = parseLocationTime(location.shift_start);
		const end = parseLocationTime(location.shift_end);
		if (!start.isValid || !end.isValid) {
			// A malformed config is surfaced, not silently dropped, and skipped for this run. Reported under
			// its own event name so it is distinguishable from the required-summing path's report.
			trackErrorEvent(
				"monthly_view_invalid_location_shift_window",
				new Error(`Malformed location shift window: ${location.shift_start}-${location.shift_end}`),
				{
					location: location.location,
					shift_start: location.shift_start,
					shift_end: location.shift_end,
				},
			);
			continue;
		}
		upsert(start, end, { occurred: false, hasTarget: true });
	}

	const eligible = [...candidates.values()].filter(
		(candidate) => candidate.hasTarget || candidate.isWrapAround || candidate.occurrences >= MONTHLY_MIN_SHIFT_WINDOW_OCCURRENCES,
	);
	const ranked = [...eligible].sort(
		(a, b) =>
			b.occurrences - a.occurrences ||
			Number(b.hasTarget) - Number(a.hasTarget) ||
			Number(b.isWrapAround) - Number(a.isWrapAround) ||
			a.start.toMillis() - b.start.toMillis(),
	);
	const chosen = ranked.slice(0, Math.max(1, maxWindows));

	/**
	 * The staffing day's wrap-around tail keeps a slot, even against the cap.
	 *
	 * Clearing the occurrence threshold is not enough on its own, and the reported department is the proof:
	 * it runs four frequent windows, so a fifth candidate — the once-a-month 2a–6p — became eligible and
	 * was then cut by the cap anyway, landing back in `Other` with no chip, no band and no cell row. A rule
	 * that survives the threshold only to lose to the cap has not made anything unloseable.
	 *
	 * So the lowest-ranked chosen window yields to the highest-ranked wrap-around one. It costs the taxonomy
	 * its least-used daytime window, which is the cheapest thing in the set to lose: that window still has a
	 * chip's worth of company in `Other`, whereas the night has nothing else that names it. Exactly one slot
	 * is reserved — a department running several nights still ranks them against each other normally.
	 *
	 * The window that yields is the lowest-ranked UNCONFIGURED one, never simply the last. A configured
	 * window is a standing promise that the department asked for bodies at that time, and the invariant
	 * above is that a fully-uncovered one still earns its chip precisely so the gap stays visible — so
	 * evicting it to make room would trade one invisible truth for another. The `hasTarget` tiebreak in
	 * `ranked` actively sorts configured windows ABOVE an equally-rare wrap-around, which is exactly what
	 * puts one at the end of `chosen`; taking the last slot blindly would hit it first. When every chosen
	 * window is configured there is nothing cheap left to spend, so the reservation stands down and the
	 * wrap-around keeps its `Other` seat.
	 */
	const topWrapAround = ranked.find((candidate) => candidate.isWrapAround);
	if (topWrapAround && !chosen.includes(topWrapAround)) {
		// Walked from the back rather than `findLastIndex`, which needs a lib newer than this project targets.
		let evictableIndex = -1;
		for (let index = chosen.length - 1; index >= 0; index -= 1) {
			if (!chosen[index].hasTarget) {
				evictableIndex = index;
				break;
			}
		}
		if (evictableIndex !== -1) chosen[evictableIndex] = topWrapAround;
	}

	chosen.sort((a, b) => a.start.toMillis() - b.start.toMillis());

	const bandCounters = new Map<ShiftType, number>();
	const windows: MonthlyShiftWindow[] = chosen.map((candidate) => {
		const band = resolveWindowBand(candidate.start, dayBoundary);
		const bandIndex = bandCounters.get(band) ?? 0;
		bandCounters.set(band, bandIndex + 1);
		return {
			key: candidate.key,
			start: candidate.start,
			end: candidate.end,
			label: formatWindowLabel(candidate.start, candidate.end),
			band,
			bandIndex,
			hasTarget: candidate.hasTarget,
		};
	});

	if (candidates.size > chosen.length) windows.push(MONTHLY_OTHER_WINDOW);
	return windows;
};

/**
 * Which of `windows` a shift belongs to: its hour-rounded window when that window is on offer, else the
 * `Other` bucket. `null` only when the window set is empty (nothing has been derived yet).
 */
export const resolveShiftWindowKey = (shift: BaseNurseShift, windowKeys: ReadonlySet<string>): string | null => {
	if (windowKeys.size === 0) return null;
	const key = roundedWindowKey(shift.startTime, shift.endTime);
	if (windowKeys.has(key)) return key;
	return windowKeys.has(MONTHLY_OTHER_WINDOW_KEY) ? MONTHLY_OTHER_WINDOW_KEY : null;
};

// ---------------------------------------------------------------------------
// Per-day aggregation
// ---------------------------------------------------------------------------

export type MonthlyJobCount = {
	jobId: string;
	jobName: string;
	count: number;
};

/** One window's row in a cell (and the tier the sidebar mirrors): the window total, split by job. */
export type MonthlyWindowCount = {
	windowKey: string;
	count: number;
	/** Non-zero jobs inside this window, in `visibleJobs` order. */
	jobs: MonthlyJobCount[];
};

export type MonthlyDayFilters = {
	/** Selected job ids; empty = all visible jobs. */
	jobFilterIds: ReadonlySet<string>;
	/** Selected staff roles (full names, as carried on a shift); empty = all roles. */
	roleFilter: ReadonlySet<string>;
	/** Selected location names; empty = all locations. */
	locationFilter: ReadonlySet<string>;
	/** Selected shift-time window key (single-select, mirroring the Day view), or `null` for all. */
	windowKey: string | null;
};

export const NO_MONTHLY_FILTERS: MonthlyDayFilters = {
	jobFilterIds: new Set(),
	roleFilter: new Set(),
	locationFilter: new Set(),
	windowKey: null,
};

/**
 * Whether a nurse works a countable shift on a day — used to HIGHLIGHT the searched staff member's days
 * in the grid. Search deliberately does not filter the staffing totals (that would undercount every other
 * day and zero out days the nurse is off); it only marks where they are scheduled.
 */
export const nurseWorksDay = (day: MonthlyDayData | undefined, showActual: boolean, nurseId: number): boolean =>
	getCountableDayShifts(day, showActual).some((shift) => shift.nurseId === nurseId);

/**
 * Whether a shift counts toward what the screen is currently showing.
 *
 * Gated on `resolveShiftJobId`, never on `shift.jobId` directly: a shift with no job resolves to the
 * explicit unassigned bucket and is counted there. Dropping it here is what used to make a day's
 * headline disagree with its own rows, its pills and the drawer, all at once and without a trace.
 */
const passesFilters = (shift: BaseNurseShift, filters: MonthlyDayFilters, visibleJobIds: ReadonlySet<string>, windowKeys: ReadonlySet<string>): boolean => {
	const jobId = resolveShiftJobId(shift);
	if (!visibleJobIds.has(jobId)) return false;
	if (filters.jobFilterIds.size > 0 && !filters.jobFilterIds.has(jobId)) return false;
	if (filters.roleFilter.size > 0 && !filters.roleFilter.has(shift.role)) return false;
	if (filters.locationFilter.size > 0 && !filters.locationFilter.has(shift.location ?? "")) return false;
	if (filters.windowKey !== null && resolveShiftWindowKey(shift, windowKeys) !== filters.windowKey) return false;
	return true;
};

type DayQuery = {
	day: MonthlyDayData | undefined;
	showActual: boolean;
	visibleJobs: Job[];
	/** The derived window set — needed to bucket a shift and to resolve the `Other` filter. */
	windows: readonly MonthlyShiftWindow[];
	filters?: MonthlyDayFilters;
};

const jobCountEntry = (job: Job, count: number): MonthlyJobCount => ({
	jobId: job.id,
	jobName: job.displayName,
	count,
});

/**
 * Per-day totals grouped by job, in `visibleJobs` order. Only jobs with a non-zero count after
 * filtering are returned, so an empty day renders no pills rather than a row of zeros.
 */
export const getJobCountsForDay = ({ day, showActual, visibleJobs, windows, filters = NO_MONTHLY_FILTERS }: DayQuery): MonthlyJobCount[] => {
	const visibleJobIds = new Set(visibleJobs.map((job) => job.id));
	const windowKeys = new Set(windows.map((window) => window.key));
	const counts = new Map<string, number>();
	for (const shift of getCountableDayShifts(day, showActual)) {
		if (!passesFilters(shift, filters, visibleJobIds, windowKeys)) continue;
		const jobId = resolveShiftJobId(shift);
		counts.set(jobId, (counts.get(jobId) ?? 0) + 1);
	}
	return visibleJobs.map((job) => jobCountEntry(job, counts.get(job.id) ?? 0)).filter((entry) => entry.count > 0);
};

/**
 * Per-day totals grouped by the derived shift-time WINDOWS, in window (start) order, each carrying its
 * per-job split. One derivation serves both the "By shift" mode (window row alone) and "Jobs & shifts"
 * (window row + nested job swatches), so the two modes and the sidebar can never disagree.
 */
export const getShiftWindowCountsForDay = ({ day, showActual, visibleJobs, windows, filters = NO_MONTHLY_FILTERS }: DayQuery): MonthlyWindowCount[] => {
	const visibleJobIds = new Set(visibleJobs.map((job) => job.id));
	const windowKeys = new Set(windows.map((window) => window.key));
	const byWindow = new Map<string, Map<string, number>>();

	for (const shift of getCountableDayShifts(day, showActual)) {
		if (!passesFilters(shift, filters, visibleJobIds, windowKeys)) continue;
		const key = resolveShiftWindowKey(shift, windowKeys);
		if (key === null) continue;
		const jobs = byWindow.get(key) ?? new Map<string, number>();
		byWindow.set(key, jobs);
		const jobId = resolveShiftJobId(shift);
		jobs.set(jobId, (jobs.get(jobId) ?? 0) + 1);
	}

	return windows
		.map((window) => {
			const jobCounts = byWindow.get(window.key);
			const jobs = visibleJobs.map((job) => jobCountEntry(job, jobCounts?.get(job.id) ?? 0)).filter((entry) => entry.count > 0);
			return { windowKey: window.key, count: jobs.reduce((sum, entry) => sum + entry.count, 0), jobs };
		})
		.filter((entry) => entry.count > 0);
};

/** A day's headline coverage state — the dominant signal in a grid cell, on the canonical tier scale. */
export type MonthlyDayCoverage = {
	/** Countable, filtered bodies scheduled (or worked) that day. */
	filled: number;
};

/**
 * The day's headcount, scoped to exactly what the cell is showing.
 *
 * No target. It used to sum every configured requirement in scope and hand back a `filled / target`
 * pair, which was wrong in two ways at once: the sum was every slot across the WHOLE day, a figure no
 * single day was meant to hit — so every tile in the month graded short — and the requirement side was
 * collected department-wide while `filled` respected the active location/role/job filters, so a filtered
 * tile compared a scoped count against unscoped demand. Nothing renders a target here now, so the honest
 * fix was to stop computing one rather than to filter a number the screen does not show.
 */
export const getDayCoverage = ({ day, showActual, visibleJobs, windows, filters = NO_MONTHLY_FILTERS }: DayQuery): MonthlyDayCoverage => ({
	filled: getShiftWindowCountsForDay({ day, showActual, visibleJobs, windows, filters }).reduce((sum, entry) => sum + entry.count, 0),
});

// ---------------------------------------------------------------------------
// Cell density — decided against the MEASURED grid, not in the abstract
// ---------------------------------------------------------------------------

/**
 * The cell's own vertical padding (px) — `p: 0.75` top and bottom, in MUI's 8px spacing unit.
 *
 * Stated rather than folded into the sum below so the chrome budget reads as what it is: padding plus
 * one control, each named by the thing that produces it.
 */
export const MONTHLY_CELL_VERTICAL_PADDING = 12;

/**
 * Reserved chrome inside a cell (px): vertical padding plus the single date/actions header row.
 *
 * The header row is exactly one `ChatActionButton` tall, so it is read from `CONTROL_HEIGHT_PX.MICRO`
 * rather than re-typed as `24`. Two literals used to stand in for that token — this budget and
 * `MonthlyJobCell`'s `CELL_HEADER_HEIGHT` — which meant raising the shared control height would have
 * silently left this grid budgeting for the old one and fitting a row that no longer had room.
 *
 * The coverage headline used to add a 26px row of its own here. It now shares the header row's right-hand
 * slot with the action bar (see `MonthlyJobCell`), which is what buys the cell a third window row at every
 * measured height instead of only on a tall screen.
 */
const CELL_CHROME_HEIGHT = MONTHLY_CELL_VERTICAL_PADDING + CONTROL_HEIGHT_PX.MICRO;
/**
 * One cell row's CONTENT height (px) — shared by the `By job` count pills AND the `By shift` window rows.
 *
 * The two used to size themselves differently (the pill grew with its own line boxes, the window row was
 * pinned at 14px), which is the main reason the two modes read as different screens rather than as two
 * breakdowns of the same tile. One constant makes "a row is a row" true by construction instead of a
 * coincidence two files have to keep re-agreeing on.
 *
 * 18 rather than the pill's old intrinsic height: it holds the 12px label and 13px count at
 * `lineHeight: 1` with room to spare, and it is what keeps THREE window rows fitting the measured
 * 1440x800 laptop cell (see `resolveCellDensity` — 3 x 20px + 36px chrome = 96 <= 104).
 */
export const MONTHLY_CELL_ROW_HEIGHT = 18;
/** The gap the cell's flex column puts under each row (theme spacing 0.25). */
const MONTHLY_CELL_ROW_GAP = 2;
/** One window row's vertical budget (px): its content plus the row gap beneath it. */
export const MONTHLY_WINDOW_ROW_HEIGHT = MONTHLY_CELL_ROW_HEIGHT + MONTHLY_CELL_ROW_GAP;
/**
 * Row height (px) at or above which a window row also has room for per-job NUMBERS. Below it a row
 * degrades to job swatch DOTS with the numbers on hover/tooltip and the full split in the sidebar.
 * Measured: 42 cells at 242×130 on a 1920×911 viewport (~150 at 1080p, ~104 on a 1440×800 laptop).
 * Height is the binding constraint AND co-varies with cell width here — both are viewport-derived — so
 * one measurement resolves both, and 132 puts the measured 130px case on dots, which is the honest call.
 */
const JOB_NUMBERS_MIN_ROW_HEIGHT = 132;
/** DEFAULT: the measured typical row height, used for the first paint before the observer reports. */
const ASSUMED_ROW_HEIGHT = 130;

/**
 * The smallest POSITIVE row budget a caller may report (px).
 *
 * `resolveCellDensity` reads `0` as "not measured yet" and substitutes the assumed 130px. That is right
 * before the observer fires and wrong once it has: an over-constrained grid — many rows, a short
 * viewport, a month divider or two — can measure a budget of zero or less, which then read as
 * "unmeasured" and bought the tallest density on the tightest possible cell, so three rows rendered into
 * a box with room for none. Clamping to this floor keeps `0` meaning exactly one thing: nobody has
 * measured. Any real measurement, however hostile, resolves to the minimum density instead.
 */
export const MONTHLY_MIN_MEASURED_ROW_HEIGHT = 1;

export type MonthlyCellDensity = {
	/** Window rows that fit under the headline; the rest collapse into a `+N` that opens the sidebar. */
	maxWindowRows: number;
	/** Whether a window row carries per-job numbers, or degrades to job swatch dots. */
	showJobNumbers: boolean;
};

/**
 * How many shift-time window rows a grid CELL draws, whatever the department derived and whatever fits.
 *
 * Three is the editorial call, not a measurement: a tile is a summary, and past three rows the eye stops
 * reading them as a shape and starts reading them as a list — that is what the day sidebar is for. So the
 * budget below is a floor test ("do 3 rows fit?"), and the cap holds even on a tall screen where 7 would.
 * Separate from `MONTHLY_MAX_SHIFT_WINDOWS`, which sizes the derived taxonomy rather than the tile.
 */
export const MONTHLY_MAX_CELL_WINDOW_ROWS = 3;

export const resolveCellDensity = (rowHeight: number): MonthlyCellDensity => {
	const height = rowHeight > 0 ? rowHeight : ASSUMED_ROW_HEIGHT;
	const fits = Math.floor((height - CELL_CHROME_HEIGHT) / MONTHLY_WINDOW_ROW_HEIGHT);
	return {
		maxWindowRows: Math.min(MONTHLY_MAX_CELL_WINDOW_ROWS, Math.max(1, fits)),
		showJobNumbers: height >= JOB_NUMBERS_MIN_ROW_HEIGHT,
	};
};

/**
 * The window rows a cell actually draws: the `limit` BUSIEST windows, handed back in the order they came
 * in — which is start-time order (`getShiftWindowCountsForDay` walks `windows`, and `deriveShiftWindows`
 * returns those sorted by start with `Other` appended last).
 *
 * Two different orders on purpose. Selecting by count is what stops a near-empty 6a window from crowding
 * out a busy 7p one purely because it happens earlier in the day; displaying by start time is what keeps
 * a row of tiles readable as a calendar, where a day's rows must run morning-to-night like every other
 * day's. Filtering the input down to the survivors — rather than re-sorting them by start afterwards —
 * is what gives the display order for free and keeps it identical to the sidebar's.
 *
 * Ties on count break on the earlier start (the earlier input index), so a day with three equal windows
 * always drops the same one and the cell cannot flicker between renders.
 */
export const selectTopWindowsForCell = (windowCounts: readonly MonthlyWindowCount[], limit: number): MonthlyWindowCount[] => {
	if (limit <= 0) return [];
	if (windowCounts.length <= limit) return [...windowCounts];
	const kept = new Set(
		windowCounts
			.map((entry, index) => ({ count: entry.count, index }))
			.sort((a, b) => b.count - a.count || a.index - b.index)
			.slice(0, limit)
			.map((entry) => entry.index),
	);
	return windowCounts.filter((_, index) => kept.has(index));
};

/** What a grid cell draws: the rows themselves plus how many PEOPLE those rows leave unaccounted for. */
export type MonthlyCellWindowRows = {
	/** The window rows the tile renders, in start-time order. Never contains the `Other` bucket. */
	rows: MonthlyWindowCount[];
	/**
	 * Scheduled people the drawn rows do NOT account for — exactly what `+N more` names, and exactly the
	 * amount by which the rows fall short of the tile's headline.
	 *
	 * PEOPLE, not groups. Counting hidden window GROUPS was the subtler version of the bug the comment
	 * below describes: a tile reading `12` over rows of `3 · 2 · 1` and a `+2 more` still cannot be added
	 * up by the person looking at it — `+2` names two things it is not drawing, which the reader has no
	 * way to turn into the six people missing from the sum. As a headcount it closes exactly:
	 * `sum(rows) + hiddenCount === headline`, always, because both sides read the same aggregation.
	 */
	hiddenCount: number;
};

/**
 * The window rows a grid CELL draws, with the band-less `Other` bucket removed first.
 *
 * `Other` is a taxonomy bucket, not a shift the department runs: it is whatever fell outside the capped
 * window set, so an `Other 6` line in a tile names no time and tells a scheduler nothing they can act on
 * while occupying one of only three rows. Dropping it here promotes the next REAL window into that row —
 * more information in the same space.
 *
 * It does NOT drop out of the hidden count, though, and that distinction is the whole point. The tile's
 * headline is the day's total (`getDayCoverage` sums the full aggregation, `Other` included), so a tile
 * that silently omits the bucket announces `16` over rows adding to `15` with nothing to explain the
 * gap — and the neighbouring days, which have no overflow, show a `+N more` it lacks, so the one day
 * with a hidden person is the one day that looks complete. A tile may decline to draw a row it cannot
 * name; it may not decline to say that the people exist. So the bucket costs no row and its people are
 * still counted, and clicking through opens the drawer, which lists them under their exact times.
 *
 * The hidden amount is therefore measured in PEOPLE — `headline − sum(drawn rows)` — and covers both
 * reasons a person can be missing from the body (a window that did not fit, and the `Other` bucket)
 * with one number the reader can actually add up. See `MonthlyCellWindowRows.hiddenCount`.
 *
 * Otherwise a CELL-level rule and nothing more: `deriveShiftWindows` still emits the bucket, the filter
 * row still offers its chip, `getShiftWindowCountsForDay` still counts it, and `buildDaySidebarData`
 * buckets by exact start/end and never consults the capped set.
 */
export const selectCellWindowRows = (windowCounts: readonly MonthlyWindowCount[], limit: number): MonthlyCellWindowRows => {
	const realWindows = windowCounts.filter((entry) => entry.windowKey !== MONTHLY_OTHER_WINDOW_KEY);
	const rows = selectTopWindowsForCell(realWindows, limit);
	const total = windowCounts.reduce((sum, entry) => sum + entry.count, 0);
	const drawn = rows.reduce((sum, entry) => sum + entry.count, 0);
	return { rows, hiddenCount: total - drawn };
};

// ---------------------------------------------------------------------------
// Sidebar drill-down model
// ---------------------------------------------------------------------------

export type MonthlySidebarEmployee = {
	shiftId: string;
	nurseId: number;
	name: string;
	role: string;
	jobId: string;
	jobName: string;
	location?: string;
	startTime: DateTime;
	endTime: DateTime;
};

export type MonthlySidebarJobGroup = {
	jobId: string;
	jobName: string;
	employees: MonthlySidebarEmployee[];
};

/**
 * One EXACT shift-time window on a day — this is where `2p–10:30p` survives the chip row's rounding.
 *
 * Every window here is one somebody is actually ON. The drawer deliberately carries NO requirement: it
 * used to also list a row per configured slot nobody was scheduled against, and grade each covered row
 * as a `have/need` fraction. Both are gone. The drawer answers "who is working this day", and a list of
 * `0`s answering a question nobody asked was the loudest thing in it.
 */
export type MonthlySidebarWindow = {
	/** Stable key for the exact start/end pair. */
	key: string;
	/**
	 * Key of the DERIVED (hour-rounded) window this belongs to. The drawer looks its band colour up by
	 * this, so a `6a–2p` cell row and its `6:00–14:30` drawer rows can never be painted differently.
	 */
	roundedKey: string;
	label: string;
	startTime: DateTime;
	endTime: DateTime;
	scheduled: number;
	jobs: MonthlySidebarJobGroup[];
	/**
	 * The job every shift in this window carries, measured BEFORE the grid's filters — `null` when the
	 * window mixes jobs (or holds none).
	 *
	 * Read `jobs.length === 1` off the filtered groups and a day filtered to Aides makes a mixed
	 * Nurse+Aide window look single-job, so posting from it would prefill "Aide" on a window that is not
	 * an Aide window. The prefill has to answer "does this window imply a job", which is a fact about the
	 * schedule, not about what the user is currently looking at.
	 */
	impliedJobId: string | null;
};

export type MonthlySidebarData = {
	totalScheduled: number;
	windowCount: number;
	windows: MonthlySidebarWindow[];
};

type SidebarBucket = { start: DateTime; end: DateTime; jobs: Map<string, MonthlySidebarEmployee[]> };

const buildJobGroups = (
	bucketJobs: Map<string, MonthlySidebarEmployee[]>,
	jobById: Map<string, Job>,
	jobOrder: Map<string, number>,
): MonthlySidebarJobGroup[] =>
	[...bucketJobs.entries()]
		.map(([jobId, employees]) => {
			const job = jobById.get(jobId);
			return {
				jobId,
				jobName: job?.displayName ?? jobId,
				employees: employees.sort((a, b) => a.name.localeCompare(b.name)),
			};
		})
		.sort((a, b) => (jobOrder.get(a.jobId) ?? 0) - (jobOrder.get(b.jobId) ?? 0));

/**
 * Builds the per-day sidebar: exact shift-time windows → jobs → employees, in start order. The same
 * filters that drive the grid apply here so the two stay consistent, and only visible jobs are included.
 *
 * Scheduled windows ONLY. The drawer used to also retain a row for every configured requirement nobody
 * was scheduled against, so an uncovered shift would surface as a coverage gap — and it used to grade
 * each covered row as a `have/need` fraction. Both are gone: a day's drawer is the list of shift times
 * somebody is working and who is on them, and a column of `0 / 2` rows for shifts nobody is on was
 * answering a question the drawer is not for. Coverage against configured demand lives in the Staffing
 * grid, which is built for it.
 *
 * Rows are ordered by the real shift instant, so a window on the wrap-around tail of the staffing day
 * sorts at the END of the day where it belongs rather than at the start of the calendar day.
 */
export const buildDaySidebarData = ({ day, showActual, visibleJobs, windows, filters = NO_MONTHLY_FILTERS }: DayQuery): MonthlySidebarData => {
	const visibleJobIds = new Set(visibleJobs.map((job) => job.id));
	const windowKeys = new Set(windows.map((window) => window.key));
	const jobById = new Map(visibleJobs.map((job) => [job.id, job]));
	const jobOrder = new Map(visibleJobs.map((job, index) => [job.id, index]));

	const buckets = new Map<string, SidebarBucket>();
	// Job cardinality per exact window BEFORE any filter, so a prefill can never claim a job that
	// filtering merely left standing alone. See `MonthlySidebarWindow.impliedJobId`.
	const unfilteredJobIds = new Map<string, Set<string>>();
	let totalScheduled = 0;

	for (const shift of getCountableDayShifts(day, showActual)) {
		const exactKey = exactWindowKey(shift.startTime, shift.endTime);
		const seenJobs = unfilteredJobIds.get(exactKey) ?? new Set<string>();
		seenJobs.add(resolveShiftJobId(shift));
		unfilteredJobIds.set(exactKey, seenJobs);

		if (!passesFilters(shift, filters, visibleJobIds, windowKeys)) continue;
		const jobId = resolveShiftJobId(shift);
		const job = jobById.get(jobId);
		if (!job) continue;
		const bucket = buckets.get(exactKey) ?? { start: shift.startTime, end: shift.endTime, jobs: new Map<string, MonthlySidebarEmployee[]>() };
		buckets.set(exactKey, bucket);
		const employees = bucket.jobs.get(jobId) ?? [];
		employees.push({
			shiftId: shift.id,
			nurseId: shift.nurseId,
			name: shift.name,
			role: shift.role,
			jobId,
			jobName: job.displayName,
			location: shift.location,
			startTime: shift.startTime,
			endTime: shift.endTime,
		});
		bucket.jobs.set(jobId, employees);
		totalScheduled += 1;
	}

	const scheduledWindows: MonthlySidebarWindow[] = [...buckets.entries()].map(([key, bucket]) => {
		const jobs = buildJobGroups(bucket.jobs, jobById, jobOrder);
		const unfiltered = unfilteredJobIds.get(key);
		// An unassigned-only window implies no job to prefill — the bucket is the absence of one.
		const soleJobId = unfiltered?.size === 1 ? [...unfiltered][0] : null;
		return {
			key,
			roundedKey: roundedWindowKey(bucket.start, bucket.end),
			label: formatWindowLabel(bucket.start, bucket.end),
			startTime: bucket.start,
			endTime: bucket.end,
			scheduled: jobs.reduce((sum, group) => sum + group.employees.length, 0),
			jobs,
			impliedJobId: soleJobId === MONTHLY_UNASSIGNED_JOB_ID ? null : soleJobId,
		};
	});

	// Start order, then the SHORTER window first where two open together, so two windows sharing a start
	// do not sit in whatever order the day's shifts happened to arrive in.
	const allWindows = [...scheduledWindows].sort((a, b) => a.startTime.toMillis() - b.startTime.toMillis() || a.endTime.toMillis() - b.endTime.toMillis());
	return { totalScheduled, windowCount: allWindows.length, windows: allWindows };
};
